/**
 * Phase 22 — Subject form portal API client.
 *
 * Public form endpoints: token-validate + submissions, (Phase 328) draft auto-save plus
 * the declarative connector-backed lookup, and (Phase 569) the thank-you screen's follow-on link.
 * No auth headers — token is bearer-equivalent in request body only.
 * Public-egress surface: no bearer injection; no token refresh logic.
 */
import type { FormValues, Theme, ValidateError, ValidateResponse } from "../types.js";
import { ApiError, engineOrigin } from "./http.js";

/**
 * Phase 329 wave 4 — `ApiError` and `engineOrigin` moved to `http.ts` when the surface grew a
 * second client (the operator-authenticated staff form). Re-exported here unchanged so every
 * existing `import { ApiError } from "../api/formApi.js"` — and every `instanceof ApiError`
 * behind it — keeps working against the SAME class object.
 */
export { ApiError };

/**
 * BB walk 2 (item 5) — a terminal answer (409 consumed / 410 expired) carries the token's tenant
 * `theme` when the engine could read it, so the "already submitted" / "expired" page is still the
 * practice's page. Absent on 404 (no tenant is known) and whenever the body carries none.
 */
export type ValidateOutcome = ValidateResponse | { error: ValidateError; theme?: Theme };

export async function validateToken(token: string): Promise<ValidateOutcome> {
  const res = await fetch(`${engineOrigin()}/forms/v0/tokens/validate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (res.status === 404) return { error: "invalid" };
  if (res.status === 410) return withTerminalTheme("expired", res);
  if (res.status === 409) return withTerminalTheme("consumed", res);
  if (!res.ok) throw new ApiError(res.status, await res.text());
  return (await res.json()) as ValidateResponse;
}

/**
 * The terminal outcome plus the tenant theme its body carried, if any. Never throws: the STATUS
 * is the answer, and an empty, unparseable or theme-less body just means the platform neutral.
 */
async function withTerminalTheme(error: ValidateError, res: Response): Promise<{ error: ValidateError; theme?: Theme }> {
  let body: unknown;
  try {
    body = await res.json();
  } catch {
    return { error };
  }
  const theme = (body as { theme?: unknown } | null)?.theme;
  return isTheme(theme) ? { error, theme } : { error };
}

/** The four required theme leaves are all strings — anything less is not a theme to apply. */
function isTheme(value: unknown): value is Theme {
  if (value === null || typeof value !== "object") return false;
  const t = value as Record<string, unknown>;
  return ["primary", "secondary", "logo_url", "favicon_url"].every((k) => typeof t[k] === "string");
}

/**
 * What the submit door acknowledges with — two fields, and deliberately nothing else.
 *
 * `status` is the job state AT ACKNOWLEDGEMENT, not the state the workflow reaches once the
 * submission has been acted on. Since phase 521 (ADR-100, the signal-door defer — it amends
 * ADR-064's webhook start door and ADR-079's operator start door) `POST /forms/v0/submissions`
 * commits the `form_submitted` trigger, pins the owed signal, stamps the job for an immediate
 * poller wake and answers. The transition that submission causes is walked OFF-REQUEST, so the
 * state named here is the one the job was sitting in when the answers became durable — usually
 * the very state the form was waiting in — and it is stale by design the moment the walk starts.
 * It is `null` outright on a replayed submission: the door's idempotent-replay arm short-circuits
 * ahead of the walk and reports no state at all, which it did before the defer too.
 *
 * So nothing here may branch on its value, and nothing does. `FormPage`'s backend discards the
 * whole outcome and reports `{kind: "submitted"}` (`formBackend.ts`), and the thank-you the
 * subject reads is client-side: the `StatusCard` "success" copy, optionally overridden by the
 * declaration's own `submitted` block carried on the VALIDATE response — never by a field of this
 * one. `fences.test.ts` pins that, because a consumer that branched on a specific state name would
 * be reading a race, and would break the day a declaration grew one more transition. Branch on the
 * call SUCCEEDING — this function throws on any non-2xx — never on the state it names.
 *
 * `event_seq` is the seq of the `form_submitted` event itself, which IS a fact at acknowledgement:
 * the row is committed before the door answers, and a replay returns the ORIGINAL seq.
 *
 * No third field: an additive "and here is what the walk did" would be a second contract to keep
 * in step for no consumer benefit, and could only be answered after the very walk this change
 * exists to get off the request.
 */
export interface SubmitOutcome { event_seq: number; status: string | null; }

/**
 * Phase 328 — `fields` widened from `Record<string, string>` to the full answer domain
 * (booleans, multi-selects, repeating-group rows). The endpoint already accepts nested JSON:
 * its per-field size guard walks the payload recursively rather than assuming flat strings.
 *
 * Resolves once the submission is DURABLE, not once the workflow has acted on it — see
 * {@link SubmitOutcome} for what that makes `status` mean.
 */
export async function submitForm(
  token: string,
  fields: FormValues,
): Promise<SubmitOutcome> {
  const res = await fetch(`${engineOrigin()}/forms/v0/submissions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, fields }),
  });
  if (!res.ok) throw new ApiError(res.status, await res.text());
  const body = (await res.json()) as { event_seq: number; status: string | null };
  return { event_seq: body.event_seq, status: body.status };
}

// ── Phase 328 wave 5 — draft auto-save ─────────────────────────────────────────────────────

/**
 * Auto-saves the token's part-completed answers: `PUT /forms/v0/drafts` with `{token, fields}`.
 *
 * <b>It never throws</b> — like {@link lookupField} and unlike {@link validateToken} /
 * {@link submitForm}. An auto-save is background work the subject never asked for and must
 * never be told about (AC-5): whatever happens to it the answers are still on the page, the
 * next change schedules another save, and the submit path does not consult a draft at all.
 * Returning a boolean instead of rejecting puts that property in the type rather than in every
 * caller's `try`.
 *
 * The draft lives ONLY server-side, scoped to the form token: there is deliberately no browser
 * copy of it (fence-asserted in `fences.test.ts`). That is the card's decision and it is a
 * privacy property, not an implementation detail — drafts expire with the token, a consumed
 * token can neither read nor write one, and a subject's part-typed identity answers are never
 * left behind on a shared or borrowed device.
 *
 * The whole answer set is sent every time, not a diff: the server keeps exactly one row per
 * token and upserts it wholesale, so the last write is the whole truth and a dropped save
 * costs nothing but the answers typed since the previous one.
 */
export async function saveDraft(token: string, fields: FormValues): Promise<boolean> {
  try {
    const res = await fetch(`${engineOrigin()}/forms/v0/drafts`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, fields }),
    });
    // 204 on success. Every failure — the token ladder (404/409/410), an oversize body (400),
    // a dead upstream (5xx) — is the same silent no-op to the subject.
    return res.ok;
  } catch {
    // Offline / DNS / CORS. Auto-save is exactly the feature that should survive a flaky
    // mobile connection without saying a word.
    return false;
  }
}

// ── Phase 328 wave 5 — the declarative connector-backed lookup ─────────────────────────────

/**
 * The lookup outcome vocabulary, mirroring the endpoint's typed degradation contract
 * (`FormsApi.LookupAsync`). `verified` is the only success; the other three are the arms the
 * subject completes the form BY HAND through.
 */
export type LookupOutcome = "verified" | "not_found" | "unavailable" | "rate_limited";

export interface LookupSuccess {
  readonly ok: true;
  readonly outcome: "verified";
  /**
   * The connector envelope, UNMAPPED, under the `response` key the declaration addresses.
   * The server deliberately does not re-map it: the schema's own `populate` /
   * `populate_group` declarations are evaluated where the field values live
   * (`schema/lookupModel.ts`), so there is exactly ONE mapping grammar, declared once.
   */
  readonly response: unknown;
}

export interface LookupDegradation {
  readonly ok: false;
  readonly outcome: Exclude<LookupOutcome, "verified">;
  /** The server's subject-facing copy when it sent one; the client has its own fallback. */
  readonly message?: string;
  /** Present on `rate_limited` when the endpoint declared a retry window. */
  readonly retry_after_seconds?: number;
}

export type LookupResult = LookupSuccess | LookupDegradation;

const UNAVAILABLE: LookupDegradation = { ok: false, outcome: "unavailable" };

/**
 * Runs ONE schema-declared lookup: `POST /forms/v0/lookups` with `{token, lookup_id, args}`.
 *
 * <b>It never throws and never rejects</b> — deliberately, and unlike its two siblings above.
 * A lookup is OPTIONAL autofill on a form the subject must always be able to finish by hand
 * (L-49), so every failure the client can experience — a typed degradation at HTTP 200, the
 * 429 bound, a token/argument rejection, a dead network, an unparseable body — resolves to a
 * typed outcome instead of an exception. That leaves the caller with no error path that could
 * disable an input or the submit button, which is the property AC-4 turns on.
 *
 * `args` carries ONLY the values of the declaration's `${fields.…}` request params. The client
 * can never name an integration, a request shape, or a connector: the endpoint resolves this
 * job's own declaration server-side from `lookup_id` (see the trust-boundary note on
 * `FormsApi.LookupAsync`).
 */
export async function lookupField(
  token: string,
  lookupId: string,
  args: Record<string, string>,
): Promise<LookupResult> {
  let res: Response;
  try {
    res = await fetch(`${engineOrigin()}/forms/v0/lookups`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token, lookup_id: lookupId, args }),
    });
  } catch {
    // Offline / DNS / CORS. Indistinguishable from an unavailable upstream at this layer, and
    // the subject needs the same instruction either way.
    return UNAVAILABLE;
  }

  return lookupResultFromBody(res.status, await readJson(res));
}

/**
 * Card #2458 — the ONE reading of a lookup response, shared by BOTH form clients: this file's
 * {@link lookupField} (the magic-link door) and `staffFormApi.ts`'s staff lookup (the signed-in
 * door). The two engine routes run one shared execution and answer one wire contract, so the
 * client reads it in one place too — a second copy is how one page ends up showing a raw code the
 * other never would.
 *
 * Total by construction: every status and every body shape lands on a typed outcome, never a throw.
 */
export function lookupResultFromBody(status: number, body: Record<string, unknown> | null): LookupResult {
  if (status === 429) {
    const retryAfter = body?.["retry_after_seconds"];
    return {
      ok: false,
      outcome: "rate_limited",
      retry_after_seconds: typeof retryAfter === "number" ? retryAfter : undefined,
    };
  }

  // 400 (bad args) / 404 (unknown token or lookup) / 409 (consumed) / 410 (expired) / 5xx —
  // all operator-facing faults. The person is told to continue manually, never shown a code.
  if (status < 200 || status >= 300 || body === null) return UNAVAILABLE;

  if (body["ok"] === true) {
    return { ok: true, outcome: "verified", response: body["response"] };
  }

  const message = typeof body["message"] === "string" ? body["message"] : undefined;
  if (body["outcome"] === "not_found") return { ok: false, outcome: "not_found", message };
  return { ok: false, outcome: "unavailable", message };
}

// ── Phase 569 (built in phase 582) — the thank-you screen's follow-on link ─────────────────

/**
 * The follow-on endpoint's CLOSED three-value wire vocabulary (`FormsApi.FollowOnAsync`). A URL
 * travels ONLY on `ready`; `pending` carries the server-owned poll floor; `unavailable` is final
 * — the form declares no follow-on, the token's window has passed, or the resolved value was
 * refused — and polling will not change it.
 */
export type FollowOnResult =
  | { readonly status: "ready"; readonly url: string }
  | { readonly status: "pending"; readonly retry_after_ms: number }
  | { readonly status: "unavailable" };

/**
 * The smallest gap the client ever leaves between two follow-on calls, in ms — the client half of
 * the server's `FormsApi.FollowOnRetryAfterMs` (pinned to it by `formApi.test.ts`, read from the
 * C# source). The server's `retry_after_ms` is a FLOOR the client honours; this constant only
 * stops a missing or too-small value from turning the poll into a hot loop.
 */
export const FOLLOW_ON_MIN_RETRY_MS = 2000;

const FOLLOW_ON_UNAVAILABLE: FollowOnResult = { status: "unavailable" };

/**
 * Asks for the declared follow-on link: `POST /forms/v0/submissions/follow-on` with `{token}`.
 *
 * <p>A SEPARATE call, deliberately not a field of the submit acknowledgement: the link lands in
 * the job ctx after the off-request walk (ADR-100), which is exactly why {@link SubmitOutcome}
 * can carry nothing about it. The token is the just-consumed form token, in the BODY like every
 * other route here, with no auth header.</p>
 *
 * <p><b>It never throws.</b> The follow-on is an optional extra on a thank-you screen that has
 * already told the subject their answers are in, so every failure — a network error, a non-2xx,
 * an unparseable or off-vocabulary body — is `unavailable`, which the page shows as the declared
 * fallback copy. A `ready` whose `url` is not an absolute https URL is `unavailable` too: the
 * server has already vetted it, and the renderer re-checks the one property that makes an
 * `<a href>` safe to render rather than trust a value it is about to hand the browser.</p>
 */
export async function fetchFollowOn(token: string): Promise<FollowOnResult> {
  let res: Response;
  try {
    res = await fetch(`${engineOrigin()}/forms/v0/submissions/follow-on`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
  } catch {
    return FOLLOW_ON_UNAVAILABLE;
  }
  if (!res.ok) return FOLLOW_ON_UNAVAILABLE;

  const body = await readJson(res);
  switch (body?.["status"]) {
    case "ready": {
      const url = body["url"];
      return typeof url === "string" && isHttpsUrl(url) ? { status: "ready", url } : FOLLOW_ON_UNAVAILABLE;
    }
    case "pending": {
      const retry = body["retry_after_ms"];
      return {
        status: "pending",
        retry_after_ms:
          typeof retry === "number" && Number.isFinite(retry)
            ? Math.max(retry, FOLLOW_ON_MIN_RETRY_MS)
            : FOLLOW_ON_MIN_RETRY_MS,
      };
    }
    default:
      return FOLLOW_ON_UNAVAILABLE;
  }
}

function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === "https:" && url.hostname !== "";
  } catch {
    return false;
  }
}

/** A JSON body, or null when there is not one (an empty 204/410 body, or malformed JSON). */
async function readJson(res: Response): Promise<Record<string, unknown> | null> {
  try {
    const parsed: unknown = await res.json();
    return typeof parsed === "object" && parsed !== null && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}
